/* DC snap scroll.

   The event page is a sequence of composed shots: the entrance, the walk, the
   floor, the hand-off, then each block below. Free scrolling let a gesture come to
   rest anywhere, including halfway through the hero's stage grow and halfway
   through a cross-fade, which is where the page looked broken. This settles every
   gesture on a beat instead.

   How it behaves, and why:
   - Nothing is jacked. The wheel, the trackpad and the keyboard all scroll the page
     normally; this only decides where the page comes to REST, once the gesture is
     over and Lenis has run out of momentum. Scrolling never fights the user.
   - A gesture never throws you backwards. If you scrolled down, the only candidates
     are stops below where the gesture began, so a long flick can settle on the next
     stop but can never yank you back to the one you just left.
   - A nudge is an undo. Move less than NUDGE of the viewport and the page returns to
     the stop the gesture started from, which is what a small stray wheel tick should
     do.
   - Out of reach means stay put. If the nearest candidate is further than REACH away
     the page is left exactly where the user put it, so a long text block can be read
     at any offset without being dragged to its edges.

   Stops are computed fresh on every gesture rather than cached, because the hero is
   pinned and grows its own stage: its scroll span moves under us, and a cached list
   would snap to where a beat used to be. One pass of getBoundingClientRect per
   gesture is nothing.

   Off entirely under reduced motion, Save-Data, without Lenis, on coarse pointers
   (touch has its own momentum and snapping on top of it fights the platform), and
   below the tablet breakpoint. */
(() => {
  if (window.__dcSnap) return;

  const MIN_W = 768;          // phones and small tablets scroll natively
  const IDLE = 90;            // ms of no input before we consider the gesture over
  const V_REST = 0.06;        // Lenis velocity under which the page counts as still
  const REACH = 1.0;          // how far a stop can be, in viewports
  const NUDGE = 0.16;         // a gesture shorter than this fraction of a viewport is an undo
  const DUR = 0.55;           // snap travel, seconds
  const MIN_MOVE = 3;         // px, below this we are already there

  /* Hero beats, as a fraction of the pinned scrub. These are the states the hero was
     composed for: the stage once it has taken the full frame with the entrance copy
     still up, the floor panel, and the hand-off. The spans between them are the
     walk, which reads fine at any offset.

     Beat 0 is deliberately absent. The pin starts one header-height down the page,
     so a stop there would sit ~110 px from the top of the document and render almost
     identically to it: a small gesture would appear to do nothing. The document top
     is already a stop and covers that shot. */
  const HERO_BEATS = [0.20, 0.72, 1];

  const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

  const start = (ev) => {
    const lenis = ev.lenis;
    if (!lenis || ev.reduced || ev.saveData) return;

    const eligible = () =>
      window.innerWidth >= MIN_W &&
      matchMedia('(pointer: fine)').matches &&
      !document.body.classList.contains('overflow-hidden');

    /* Every stop on the page, in document order. */
    const stops = () => {
      const out = [0];
      const heroRoot = document.querySelector('.dc-hero');
      const st = heroRoot && heroRoot.__dcHero && heroRoot.__dcHero.motion
        && heroRoot.__dcHero.motion.tween && heroRoot.__dcHero.motion.tween.scrollTrigger;
      if (st && st.end > st.start) {
        const span = st.end - st.start;
        HERO_BEATS.forEach((n) => out.push(st.start + span * n));
      }
      document.querySelectorAll('.dc-section').forEach((el) => {
        if (heroRoot && (el === heroRoot || el.contains(heroRoot))) return;
        out.push(el.getBoundingClientRect().top + window.scrollY);
      });
      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const seen = [];
      out.sort((a, b) => a - b).forEach((v) => {
        const y = Math.round(Math.min(Math.max(v, 0), max));
        if (!seen.length || y - seen[seen.length - 1] > 8) seen.push(y);
      });
      return seen;
    };

    const state = { armed: false, from: null, dir: 0, snapping: false, idle: 0, poll: 0 };

    const cancelSnap = () => {
      if (!state.snapping) return;
      state.snapping = false;
      /* Hand control straight back: re-target Lenis at where it actually is, so an
         in-flight snap cannot keep pulling against the new gesture. */
      try { lenis.scrollTo(lenis.animatedScroll, { immediate: true, force: true }); } catch (e) {}
    };

    const choose = (from, to, dir) => {
      const list = stops();
      if (!list.length) return null;
      const reach = window.innerHeight * REACH;
      const travel = Math.abs(to - from);

      /* A nudge goes back where it came from. */
      if (travel < window.innerHeight * NUDGE) {
        let best = null, bd = Infinity;
        for (const p of list) { const d = Math.abs(p - from); if (d < bd) { bd = d; best = p; } }
        return bd <= reach ? best : null;
      }

      /* Otherwise the nearest stop that lies ahead of where the gesture began. */
      let best = null, bd = Infinity;
      for (const p of list) {
        if (dir > 0 && p <= from + 4) continue;
        if (dir < 0 && p >= from - 4) continue;
        const d = Math.abs(p - to);
        if (d < bd) { bd = d; best = p; }
      }
      return best != null && bd <= reach ? best : null;
    };

    const settle = () => {
      state.poll = 0;
      if (!state.armed) return;
      if (Math.abs(lenis.velocity || 0) > V_REST) { state.poll = requestAnimationFrame(settle); return; }
      state.armed = false;
      if (!eligible()) return;
      const to = lenis.animatedScroll;
      const target = choose(state.from, to, state.dir);
      if (target == null || Math.abs(target - to) < MIN_MOVE) return;
      state.snapping = true;
      try {
        lenis.scrollTo(target, {
          duration: DUR,
          easing: easeOutExpo,
          lock: false,
          force: true,
          onComplete: () => { state.snapping = false; },
        });
      } catch (e) { state.snapping = false; }
    };

    const onInput = (dir) => {
      if (!eligible()) return;
      cancelSnap();
      if (!state.armed) { state.armed = true; state.from = lenis.animatedScroll; }
      if (dir) state.dir = dir;
      clearTimeout(state.idle);
      if (state.poll) { cancelAnimationFrame(state.poll); state.poll = 0; }
      state.idle = setTimeout(() => { state.poll = requestAnimationFrame(settle); }, IDLE);
    };

    window.addEventListener('wheel', (e) => onInput(Math.sign(e.deltaY)), { passive: true });
    window.addEventListener('keydown', (e) => {
      const t = e.target;
      if (t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName))) return;
      const down = ['ArrowDown', 'PageDown', 'End', ' ', 'Spacebar'].indexOf(e.key) > -1;
      const up = ['ArrowUp', 'PageUp', 'Home'].indexOf(e.key) > -1;
      if (down || up) onInput(down ? 1 : -1);
    }, { passive: true });

    /* Anything that deliberately moves the page (the chapter rail, an anchor, the
       hero's own setProgress) is not a gesture: drop any pending snap so it cannot
       fire on top of the destination. */
    document.addEventListener('click', () => {
      state.armed = false;
      clearTimeout(state.idle);
      if (state.poll) { cancelAnimationFrame(state.poll); state.poll = 0; }
    }, true);

    window.__dcSnap = {
      version: '1',
      get stops() { return stops(); },
      get state() { return state; },
      beats: HERO_BEATS,
      disable() { state.armed = false; clearTimeout(state.idle); cancelSnap(); },
    };
  };

  if (window.__dcEvent) start(window.__dcEvent);
  else document.addEventListener('dc:event:ready', (e) => start(e.detail), { once: true });
})();
